import { t } from "@lingui/core/macro";
import { useNavigate, useMatches } from "react-router-dom";
import { useSelector } from "react-redux";
import { CircleQuestionMark } from "lucide-react";

import { SidebarTrigger } from "src/@/components/ui/sidebar";
import { Separator } from "src/@/components/ui/separator";
import { Breadcrumb } from "src/@/components/ui/breadcrumb";
import {
    HybridTooltip,
    HybridTooltipTrigger,
    HybridTooltipContent,
} from "src/@/components/ui/HybridTooltip";

import { RouteHandle } from "#navigation/types";
import config from "#config";
import { getUserIsSnackpassEmployee } from "src/redux/selectors/user";

export function NavigationHeader() {
    const navigate = useNavigate();
    const matches = useMatches();
    const isSnackpassEmployee = useSelector(getUserIsSnackpassEmployee);

    const TITLE_TRANSLATIONS = {
        //Title pages
        Store: t`Store`,
        Marketing: t`Marketing`,
        Data: t`Data`,
        Tools: t`Tools`,
        Settings: t`Settings`,

        //Init pages
        Dashboard: t`Dashboard`,
        Orders: t`Orders`,
        "Order Details": t`Order Details`,
        "Purchase Details": t`Purchase Details`,
        Menu: t`Menu`,
        Items: t`Items`,
        Menus: t`Menus`,
        "New Menu": t`New Menu`,
        "Edit Menu": t`Edit Menu`,
        "Menu Manager": t`Menu Manager`,
        Categories: t`Categories`,
        "Third Party": t`Third Party`,
        "Create Menu": t`Create Menu`,
        "Menu Settings": t`Menu Settings`,
        Ingredients: t`Ingredients`,

        // Marketing section
        Conversations: t`Conversations`,
        Guestbook: t`Guestbook`,
        Customers: t`Customers`,
        "Audit Log": t`Audit Log`,
        Promotions: t`Promotions`,
        Create: t`Create`,
        "Create Promotion": t`Create Promotion`,
        "Gift Cards": t`Gift Cards`,
        "SMS Campaigns": t`SMS Campaigns`,
        "Push Campaigns": t`Push Campaigns`,
        "Create Campaign": t`Create Campaign`,
        "Create Campaign (Internal)": t`Create Campaign (Internal)`,

        // Data/Reports section
        Sales: t`Sales`,
        Reports: t`Reports`,
        Hourly: t`Hourly`,
        "Period Comparisons": t`Period Comparisons`,
        Payouts: t`Payouts`,
        Labor: t`Labor`,
        "Time Cards": t`Time Cards`,
        Tips: t`Tips`,
        "More Reports": t`More Reports`,
        "Third Parties": t`Third Parties`,
        Overview: t`Overview`,
        Balances: t`Balances`,
        Transactions: t`Transactions`,
        Liabilities: t`Liabilities`,
        Cash: t`Cash`,
        Locations: t`Locations`,

        // Tools section
        "QR Codes": t`QR Codes`,
        Catering: t`Catering`,
        Invoices: t`Invoices`,
        Integrations: t`Integrations`,

        // Settings section
        Team: t`Team`,
        Users: t`Users`,
        "Clock In and Out": t`Clock In and Out`,
        Devices: t`Devices`,
        "Front of House": t`Front of House`,
        "Brand and Appearance": t`Brand and Appearance`,
        Lookbook: t`Lookbook`,
        Tipping: t`Tipping`,
        "All Orders": t`All Orders`,
        "Online Orders": t`Online Orders`,
        "Kiosk and Register": t`Kiosk and Register`,
        "Back of House": t`Back of House`,
        "Order Flow": t`Order Flow`,
        "Prep Stations": t`Prep Stations`,
        "Table Editor": t`Table Editor`,
        Machines: t`Machines`,
        "Account & Tax": t`Account & Tax`,
        "Bank Account": t`Bank Account`,
        "Payout Schedule": t`Payout Schedule`,
        "Tax Rates": t`Tax Rates`,
        "Tax Forms": t`Tax Forms`,
        Billing: t`Billing`,
        Contract: t`Contract`,
        Other: t`Other`,
        Products: t`Products`,
        Legal: t`Legal`,
        "Snackpass Admin": t`Snackpass Admin`,
        Internal: t`Internal`,
        Fees: t`Fees`,
        "Senior Support": t`Senior Support`,

        // Account pages
        Account: t`Account`,
        Teams: t`Teams`,
        "Accept Invite": t`Accept Invite`,
        "Not Allowed": t`Not Allowed`,
        "Not Found": t`Not Found`,
        Logout: t`Logout`,
    };

    // Function to translate a title
    const translateTitle = (title: string): string => {
        const translation =
            TITLE_TRANSLATIONS[title as keyof typeof TITLE_TRANSLATIONS];
        if (translation) {
            return translation;
        }
        // If no translation found, return the original title
        return title;
    };

    const bcItems = matches
        .filter(({ handle }) => Boolean((handle as RouteHandle)?.title)) // if the match doesn't have a title, ignore it
        .map(({ pathname, handle }) => ({
            url: pathname === "/" ? null : pathname, // if pathname is "/", there is not a page or component to navigate to
            title: translateTitle((handle as RouteHandle).title ?? "..."),
        }));

    // Check if we can go back in history and the previous page was from our site
    const canGoBack =
        window.history.state?.idx > 0 &&
        document.referrer.startsWith(window.location.origin);

    return (
        <header className="sticky top-0 z-10 flex h-16 shrink-0 items-center justify-between gap-2 rounded-xl bg-background">
            <div className="flex items-center gap-2 px-4">
                {/* not working well. comment out for now  */}
                {/* {canGoBack ? (
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => navigate(-1)}
                    >
                        <ArrowLeft className="h-4 w-4" />
                    </Button>
                ) : null} */}
                <SidebarTrigger className="-ml-1" />
                <Separator orientation="vertical" className="mr-2 h-4" />
                <Breadcrumb items={bcItems} />
            </div>
            {isSnackpassEmployee && (
                <div className="mr-4">
                    <HybridTooltip>
                        <HybridTooltipTrigger asChild>
                            <div
                                className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium text-white shadow-sm ${
                                    config.isProduction
                                        ? "bg-orange-500"
                                        : "bg-green-500"
                                }`}
                            >
                                {config.env.toUpperCase()}{" "}
                                <span className="ml-1">
                                    <CircleQuestionMark
                                        style={{ width: "1em", height: "1em" }}
                                    />
                                </span>
                            </div>
                        </HybridTooltipTrigger>
                        <HybridTooltipContent
                            className={
                                "z-100 w-max max-w-sm overflow-auto break-words rounded-md bg-neutral-900 px-3 py-2 text-center font-normal text-neutral-50"
                            }
                            sideOffset={20}
                            side="bottom"
                        >
                            <p>{`You are currently using the ${config.env} dashboard. This element is only visible to Snackpass employees.`}</p>
                        </HybridTooltipContent>
                    </HybridTooltip>
                </div>
            )}
        </header>
    );
}
